<script lang="ts">
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import StatusTag from "@kontextwerk/web-sdk/ui/display/StatusTag.svelte"
  import { mdiCheckAll, mdiPlusCircleOutline } from "@mdi/js"
  import CreateWorkflowActionBox from "../../../../../../../../../../_shared/_modules/create-workflow-action-box"
  import { SCHEDULING_MINUTE_STEP } from "../../../../../../../../../../../../../../_shared/_logic/time-granularity"
  import type {
    BlockerDurationMode,
    BlockerPlanningKind,
    BlockerSeriesPattern,
  } from "../../../../_shared/_logic/create-blocker-contract"
  import {
    BLOCKER_WEEKDAY_LABELS,
    blockerTimingStartsInPast,
    buildBlockerSeriesPreview,
    formatBlockerCalendarDay,
    normalizeBlockerCalendarDay,
    normalizeBlockerDuration,
    normalizeBlockerStartTime,
  } from "../../_shared/_logic/blocker-timing"

  type Props = {
    planningKind?: BlockerPlanningKind
    durationMode?: BlockerDurationMode
    singleDate?: Date | null
    startTime?: string
    durationMinutes?: number | null
    seriesRangeStart?: Date | null
    seriesRangeEnd?: Date | null
    seriesPattern?: BlockerSeriesPattern
    seriesWeekdays?: number[]
    readOnly?: boolean
  }

  let {
    planningKind = $bindable<BlockerPlanningKind>("series"),
    durationMode = $bindable<BlockerDurationMode>("all_day"),
    singleDate = $bindable<Date | null>(null),
    startTime = $bindable("09:00"),
    durationMinutes = $bindable<number | null>(60),
    seriesRangeStart = $bindable<Date | null>(null),
    seriesRangeEnd = $bindable<Date | null>(null),
    seriesPattern = $bindable<BlockerSeriesPattern>("WEEKLY"),
    seriesWeekdays = $bindable<number[]>([]),
    readOnly = false,
  }: Props = $props()

  const durationModeOptions = [
    { name: "Ganztägig", value: "all_day" },
    { name: "Individuell", value: "individual" },
  ]
  const fieldSurfaceTone = "dark" as const
  const seriesDateRange = $derived({ start: seriesRangeStart, end: seriesRangeEnd })
  const seriesPreview = $derived(
    buildBlockerSeriesPreview({
      planningKind,
      seriesRangeStart,
      seriesRangeEnd,
      seriesPattern,
      seriesWeekdays,
    }),
  )
  const selectedWeekdayLabels = $derived(
    BLOCKER_WEEKDAY_LABELS.filter((_, index) => seriesWeekdays.includes(index)).join(", ") ||
      "nicht hinterlegt",
  )
  const startInPast = $derived(
    blockerTimingStartsInPast({
      planningKind,
      durationMode,
      singleDate,
      startTime,
      seriesRangeStart,
    }),
  )

  const setDurationMode = (value: unknown) => {
    durationMode = value === "individual" ? "individual" : "all_day"
  }

  const setSeriesRange = (value: unknown) => {
    if (!value || typeof value !== "object") {
      seriesRangeStart = null
      seriesRangeEnd = null

      return
    }

    const range = value as { start?: unknown; end?: unknown }
    seriesRangeStart = normalizeBlockerCalendarDay(range.start)
    seriesRangeEnd = normalizeBlockerCalendarDay(range.end)
  }

  const handleSeriesRangeChange = (event: Event) => {
    const detail = (event as CustomEvent<{ value?: unknown }>).detail
    setSeriesRange(detail?.value)
  }

  const normalizeStartTime = () => {
    const normalized = normalizeBlockerStartTime(startTime)

    if (normalized) startTime = normalized
  }

  const normalizeDuration = () => {
    const normalized = normalizeBlockerDuration(durationMinutes)

    if (normalized) durationMinutes = normalized
  }

  const toggleWeekday = (weekday: number) => {
    const selected = new Set(seriesWeekdays)

    if (selected.has(weekday)) {
      if (selected.size <= 1) return

      selected.delete(weekday)
    } else {
      selected.add(weekday)
    }

    seriesWeekdays = Array.from(selected).sort((left, right) => left - right)
  }
</script>

<CreateWorkflowActionBox label="Zeit und Wiederholung">
  {#if readOnly}
    <dl class="timing-summary">
      <div>
        <dt>Planung</dt>
        <dd>{planningKind === "series" ? "Serie" : "Einzel"}</dd>
      </div>
      <div>
        <dt>Dauer</dt>
        <dd>{durationMode === "all_day" ? "Ganztägig" : `${durationMinutes ?? "–"} Minuten`}</dd>
      </div>
      {#if planningKind === "series"}
        <div>
          <dt>Zeitraum</dt>
          <dd
            >{formatBlockerCalendarDay(seriesRangeStart)} – {formatBlockerCalendarDay(
              seriesRangeEnd,
            )}</dd
          >
        </div>
        <div>
          <dt>Tage</dt>
          <dd>{selectedWeekdayLabels}</dd>
        </div>
      {:else}
        <div>
          <dt>Datum</dt>
          <dd>{formatBlockerCalendarDay(singleDate)}</dd>
        </div>
      {/if}
      {#if durationMode === "individual"}
        <div>
          <dt>Start</dt>
          <dd>{startTime || "nicht hinterlegt"} Uhr</dd>
        </div>
      {/if}
      {#if planningKind === "series" && seriesPreview && !seriesPreview.error}
        <div>
          <dt>Einträge</dt>
          <dd>{seriesPreview.count}</dd>
        </div>
      {/if}
    </dl>
  {:else}
    <div class="create-workflow-field-grid">
      {#if planningKind === "series"}
        <Input
          type="dateRange"
          placeholder="Zeitraum"
          value={seriesDateRange}
          dateGranularity="day"
          withTime={false}
          surfaceTone={fieldSurfaceTone}
          onChange={handleSeriesRangeChange}
          dataTestid="blocker-timing.series-range"
        />
      {:else}
        <Input
          type="date"
          placeholder="Datum"
          bind:value={singleDate}
          dateGranularity="day"
          withTime={false}
          surfaceTone={fieldSurfaceTone}
          dataTestid="blocker-timing.single-date"
        />
      {/if}
      <Input
        type="select"
        placeholder="Dauer"
        value={durationMode}
        options={durationModeOptions}
        surfaceTone={fieldSurfaceTone}
        onValueChange={setDurationMode}
        dataTestid="blocker-timing.duration-mode"
      />
    </div>

    {#if durationMode === "individual"}
      <div class="create-workflow-field-grid">
        <Input
          type="time"
          placeholder="Start (Uhrzeit)"
          bind:value={startTime}
          step={SCHEDULING_MINUTE_STEP * 60}
          surfaceTone={fieldSurfaceTone}
          onChange={normalizeStartTime}
          dataTestid="blocker-timing.start-time"
        />
        <Input
          type="number"
          placeholder="Dauer (Minuten)"
          bind:value={durationMinutes}
          min={SCHEDULING_MINUTE_STEP}
          step={SCHEDULING_MINUTE_STEP}
          surfaceTone={fieldSurfaceTone}
          onChange={normalizeDuration}
          dataTestid="blocker-timing.duration-minutes"
        />
      </div>
    {/if}

    {#if planningKind === "series"}
      <div class="weekday-field">
        <div class="weekday-label">Tage</div>
        <div class="weekday-toggles">
          {#each BLOCKER_WEEKDAY_LABELS as label, index (label)}
            {@const active = seriesWeekdays.includes(index)}
            <button
              type="button"
              class="selectable-status-tag-button"
              aria-label={`${label} ${active ? "abwählen" : "auswählen"}`}
              aria-pressed={active}
              onclick={() => toggleWeekday(index)}
            >
              <StatusTag
                variant="status"
                size="sm"
                {label}
                color={active ? "emerald" : "#64748b"}
                icon={active ? mdiCheckAll : mdiPlusCircleOutline}
                disableTitle={true}
              />
            </button>
          {/each}
        </div>
      </div>
      {#if seriesPreview}
        {#if seriesPreview.error}
          <div class="inline-error">{seriesPreview.error}</div>
        {:else}
          <div class="series-preview">Erzeugt voraussichtlich {seriesPreview.count} Einträge.</div>
        {/if}
      {/if}
    {/if}
  {/if}
</CreateWorkflowActionBox>

{#if startInPast && !readOnly}
  <div class="inline-warning">
    Hinweis: Die Startzeit liegt in der Vergangenheit. Kehrblatt prüft das beim Anwenden.
  </div>
{/if}

<style lang="less">
  .weekday-field {
    margin: 0.15rem 0 0.25rem;
  }

  .weekday-label,
  .timing-summary dt {
    margin-bottom: 0.35rem;
    color: var(--text-100);
    font-size: 0.72rem;
    font-weight: 800;
    text-transform: uppercase;
  }

  .weekday-toggles {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
  }

  .selectable-status-tag-button {
    display: inline-flex;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    cursor: pointer;
  }

  .series-preview {
    color: color-mix(in srgb, var(--text-100) 76%, transparent);
    font-size: 0.85rem;
  }

  .inline-error {
    color: var(--error-100);
    font-size: 0.85rem;
  }

  .inline-warning {
    margin-top: 0.25rem;
    color: color-mix(in srgb, #b45309 78%, var(--text-100));
    font-size: 0.85rem;
    line-height: 1.35;
  }

  .timing-summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
    gap: 0.75rem 1rem;
    margin: 0;
  }

  .timing-summary div {
    min-width: 0;
  }

  .timing-summary dt {
    margin: 0;
    color: color-mix(in srgb, var(--text-100) 72%, transparent);
  }

  .timing-summary dd {
    margin: 0.1rem 0 0;
    color: var(--text-100);
    line-height: 1.3;
    overflow-wrap: anywhere;
  }
</style>
